import React from "react";
import type { Temporal, TemporalValue as TemporalValueType } from "../../../utils";
import Chip from "../../common/Chip/Chip";
import Icon from "../../common/Icon/Icon";
import TemporalBarView from "./TemporalBarView";
import TemporalChartView from "./TemporalChartView";
import TemporalModal from "./TemporalModal";
import TemporalTextView from "./TemporalTextView";
import { normalizeRanges } from "./utils";

export type TemporalTextProps = {
  maxEntries?: number;
  scrollable?: boolean;
};

export type BaseTemporalValueProps = {
  value?: TemporalValueType | Temporal | null;
  node?: any;
  inline?: boolean;
  className?: string;
  textProps?: TemporalTextProps;
  forceType?: "bar" | "chart" | "text";
  temporalChartHeight?: number;
  edit?: boolean;
  onPopUp?: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  baseStyles?: boolean;
};

const BaseTemporalValue = (props: BaseTemporalValueProps) => {
  const {
    value,
    node,
    inline = false,
    className,
    textProps,
    forceType,
    temporalChartHeight,
    edit = false,
    onPopUp,
    onClick,
    baseStyles = true,
  } = props;

  const temporal = (value && (value as any).temporal ? (value as any).temporal : value) as Temporal | undefined;

  const rangesRaw = temporal?.ranges || [];
  const ranges = normalizeRanges(rangesRaw);

  const validTypes = ["bar", "chart", "text"] as const;
  const [type, setType] = React.useState<(typeof validTypes)[number]>(
    (forceType as any) || (localStorage.getItem("decisively.temporalViewPref") as any) || "bar",
  );
  const [timelineOpen, setTimelineOpen] = React.useState(false);

  React.useEffect(() => {
    const handleStorageChange = () => {
      const newType = localStorage.getItem("decisively.temporalViewPref") as any;
      if (newType && (validTypes as readonly string[]).includes(newType)) setType(newType as any);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  if (!temporal || !Array.isArray(ranges) || ranges.length === 0) {
    return <span className={className}>Invalid Value</span>;
  }

  const toggleView = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const newType = type === "bar" ? "chart" : type === "chart" ? "text" : "bar";
    setType(newType);
    localStorage.setItem("decisively.temporalViewPref", newType);
    window.dispatchEvent(new Event("storage"));
  };

  const handleHelp = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTimelineOpen(true);
  };

  const maxEntries = textProps?.maxEntries && textProps.maxEntries > 0 ? textProps.maxEntries : ranges.length;

  const activeView = () => {
    switch (type) {
      case "chart":
        return (
          <TemporalChartView
            ranges={ranges}
            height={temporalChartHeight}
          />
        );
      case "bar":
        return (
          <TemporalBarView
            value={{ temporal: { ranges } }}
            title={node?.description}
            usePercentageWidth={true}
            onClick={onPopUp as any}
          />
        );
      default:
        return (
          <TemporalTextView
            value={{ temporal: { ranges } }}
            maxEntries={maxEntries}
          />
        );
    }
  };

  return (
    <>
      {/* instead of width 100%, use flex to our advantage and its behaviour to auto fill when set to minWidth 0 */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}>
        {!inline ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              aria-label="Toggle temporal view"
              onClick={toggleView}
              style={{ background: "transparent", border: 0, padding: 2, cursor: "pointer" }}
            >
              <Icon name="refresh" />
            </button>
            <button
              type="button"
              aria-label="Help"
              onClick={handleHelp}
              style={{ background: "transparent", border: 0, padding: 2, cursor: "pointer" }}
            >
              <Icon name="help" />
            </button>
            {edit ? (
              <button
                type="button"
                aria-label="Edit"
                onClick={(e) => e.stopPropagation()}
                style={{ background: "transparent", border: 0, padding: 2, cursor: "pointer" }}
              >
                <Icon name="edit" />
              </button>
            ) : null}
          </div>
        ) : null}
        {/* <div
          style={{ flexGrow: type === "bar" ? 1 : 0, width: "100%", display: "flex" }}
          onClick={onClick}
        > */}
        {activeView()}
        {/* </div> */}
        {!inline && typeof node?.input !== "undefined" ? (
          <div style={{ marginLeft: 8 }}>
            <Chip
              color="#111827"
              backgroundColor="#E5E7EB"
            >
              Input
            </Chip>
          </div>
        ) : null}
      </div>
      <TemporalModal
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        ranges={ranges}
        node={node}
      />
    </>
  );
};

export default BaseTemporalValue;
